import React, { useState, useEffect, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { VOICE_SHORTCUT_DOWN, VOICE_SHORTCUT_UP } from '../hooks/useVoiceShortcut';
import {
  Minus, Copy, Maximize, Check,
  Heading1, Heading2, Heading3, Pilcrow,
  Indent, ListOrdered, Wand2, Loader2, X
} from 'lucide-react';
import { styles, ToolBtn } from './NodeCommons';
import QuickTooltip from '../components/QuickTooltip';
import InlineColorPicker from '../components/InlineColorPicker';
import VoiceMicButton from '../components/VoiceMicButton'; // 🎤 语音输入按钮
import { useTheme } from '../hooks/useTheme';
import api, { getToken } from '../api';
import { API_BASE_URL } from '../constants';
import { copyTextToClipboard } from '../utils/browserCompat'; // http 站点没有 navigator.clipboard

// --- 1. 色板更新：高级灰 / 暗彩色系 (用户定制) ---
const BG_COLORS = [
  '#1e293b', // 1. Slate (默认深蓝灰)
  '#262626', // 2. Neutral (中性深黑灰)
  '#3e2626', // 3. Red tint (暗褐红灰)
  '#3e3226', // 4. Brown tint (暗咖啡灰)
  '#2e3626', // 5. Green tint (暗橄榄灰)
  '#26303e', // 6. Blue tint (暗普鲁士蓝灰)
  '#2e263e', // 7. Purple tint (暗茄紫灰)
  '#3e2636', // 8. Pink tint (暗胭脂灰)
];
// 浅色模式下对应的浅色调（同 index 一一对应）。用户选中一个色后，
// 深色模式显深调、浅色模式显对应浅调，两模式都协调。
const BG_COLORS_LIGHT = [
  '#8a94a3', // 1. Slate（中等灰，配近白字）
  '#909094', // 2. Neutral
  '#a38a8a', // 3. Red tint
  '#9d9384', // 4. Brown tint
  '#8b9781', // 5. Green tint
  '#8693a3', // 6. Blue tint
  '#928aa0', // 7. Purple tint
  '#9d8a95', // 8. Pink tint
];
// 老版「新建咒语」用的是另一套随机深色调色板(editorUtils 的 TEXT_BG_COLORS)，
// 与取色器 BG_COLORS 几乎不重叠。这里把两套深色都映射到浅色，保证老节点也能翻。
const LEGACY_BG_MAP = {
  '#1e293b': '#8a94a3', // slate
  '#2a2a2a': '#909094', // neutral
  '#2b211e': '#9d9384', // 红褐
  '#2c2520': '#9d9384', // 咖啡
  '#272e2b': '#8b9781', // 橄榄绿
  '#2c2a3e': '#928aa0', // 茄紫
  '#4a5568': '#8a94a3', // slate 亮
  '#334155': '#8a94a3', // slate 深
};
// 存储值恒为深色调 hex；渲染时按主题翻译。命中任一预设→取对应浅调，自定义色→原样返回。
export const resolveBgForTheme = (c, theme) => {
  if (theme !== 'light' || !c) return c;
  const i = BG_COLORS.indexOf(c);
  if (i >= 0) return BG_COLORS_LIGHT[i];
  return LEGACY_BG_MAP[String(c).toLowerCase()] || c;
};

const ToolbarButton = ({ active, onClick, title, children }) => {
  return (
    <ToolBtn onClick={onClick} title={title} active={active}>
      {children}
    </ToolBtn>
  );
};

const Divider = () => (
  <div style={{ width: '1px', height: '14px', background: 'var(--border-strong)', margin: '0 4px' }} />
);

const ToolbarContent = ({
  fontSize, fontWeight, isIndented, showColorPicker, toggleColorPicker,
  bgColor, setPreset, toggleIndent, insertDivider, insertNumbering,
  copyText, toggleFullScreen, updateBgColor, isFullMode, theme
}) => (
  <>
    {!isFullMode && (
      <>
        <QuickTooltip content="背景颜色" position="top" >
          <div 
             onClick={toggleColorPicker} 
             style={{
               width: '28px', height: '28px', borderRadius: '6px', cursor: 'pointer',
               display: 'flex', alignItems: 'center', justifyContent: 'center',
               background: showColorPicker ? 'var(--bg-hover)' : 'transparent',
               position: 'relative',
               transition: 'all 0.1s'
             }}
             onMouseEnter={(e) => e.currentTarget.style.background = 'var(--bg-hover)'}
             onMouseLeave={(e) => !showColorPicker && (e.currentTarget.style.background = 'transparent')}
          >
             <div style={{
               width: '14px', height: '14px', borderRadius: '50%',
               background: resolveBgForTheme(bgColor, theme),
               border: '1px solid rgba(255,255,255,0.3)', boxShadow: '0 0 4px rgba(0,0,0,0.3)'
             }} />

             {/* --- 修正：向下弹出的色盘 (top: 36px) --- */}
             {showColorPicker && (
                <div style={{
                  position: 'absolute', top: '36px', left: '50%', transform: 'translateX(-50%)',
                  display: 'flex', flexDirection: 'column', gap: '8px', padding: '8px',
                  background: 'var(--bg-panel)', borderRadius: '50px', border: '1px solid var(--border-strong)',
                  boxShadow: 'var(--shadow-panel)', zIndex: 2000
                }} onClick={e => e.stopPropagation()}>
                  {BG_COLORS.map(c => (
                    <div
                      key={c}
                      onClick={() => updateBgColor(c)}
                      style={{
                        width: '20px', height: '20px', borderRadius: '50%',
                        background: resolveBgForTheme(c, theme),
                        border: bgColor === c ? '2px solid var(--text-hover)' : '1px solid var(--border-strong)',
                        cursor: 'pointer', flexShrink: 0, transition: 'transform 0.1s'
                      }}
                      onMouseEnter={e => e.currentTarget.style.transform = 'scale(1.2)'}
                      onMouseLeave={e => e.currentTarget.style.transform = 'scale(1)'}
                    />
                  ))}
                  {/* 自定义颜色按钮 */}
                  <InlineColorPicker
                    color={bgColor}
                    rainbow
                    size={20}
                    onChange={c => updateBgColor(c)}
                  />
                </div>
             )}
          </div>
        </QuickTooltip>
        <Divider />
      </>
    )}

    <ToolbarButton active={fontSize === 24} onClick={() => setPreset('h1')} title="标题 1"><Heading1 size={18} strokeWidth={1.5} /></ToolbarButton>
    <ToolbarButton active={fontSize === 20} onClick={() => setPreset('h2')} title="标题 2"><Heading2 size={18} strokeWidth={1.5} /></ToolbarButton>
    <ToolbarButton active={fontSize === 16} onClick={() => setPreset('h3')} title="标题 3"><Heading3 size={18} strokeWidth={1.5} /></ToolbarButton>
    <ToolbarButton active={fontSize === 12 && fontWeight === 'normal'} onClick={() => setPreset('p')} title="正文"><Pilcrow size={16} strokeWidth={1.5} /></ToolbarButton>

    <Divider />

    <ToolbarButton active={isIndented} onClick={toggleIndent} title="首行缩进"><Indent size={18} strokeWidth={1.5} /></ToolbarButton>
    <ToolbarButton active={false} onClick={insertNumbering} title="自动编号"><ListOrdered size={18} strokeWidth={1.5} /></ToolbarButton>
    <ToolbarButton active={false} onClick={insertDivider} title="插入横线"><Minus size={18} strokeWidth={1.5} /></ToolbarButton>

    <Divider />

    <ToolbarButton active={false} onClick={copyText} title="复制全部"><Copy size={16} strokeWidth={1.5} /></ToolbarButton>
    
    {!isFullMode && (
      <ToolbarButton active={false} onClick={toggleFullScreen} title="全屏编辑"><Maximize size={16} strokeWidth={1.5} /></ToolbarButton>
    )}
  </>
);

const FullScreenModal = ({ isOpen, onClose, text, onChange, toolbarProps, forceFocusKey }) => {
  const fsTextareaRef = useRef(null);
  const lastFocusTriggerRef = useRef(forceFocusKey);
  const [textPolishEnabled, setTextPolishEnabled] = useState(false);
  const [isPolishing, setIsPolishing] = useState(false);
  const [polishPreview, setPolishPreview] = useState(null);
  const [polishLocalText, setPolishLocalText] = useState('');
  const [polishEditedResult, setPolishEditedResult] = useState('');
  const polishUserEditedRef = useRef(false);
  const polishAbortRef = useRef(null);
  const polishTextareaRef = useRef(null);

  useEffect(() => {
    const checkPolish = () => {
      // 文本润色按钮显隐：取决于「文本润色设定」里是否勾选了至少一个语言模型
      api.get('/api/ai/text-polish/models').then(d => {
        setTextPolishEnabled(Array.isArray(d?.models) && d.models.length > 0);
      }).catch(() => {});
    };
    checkPolish();
    window.addEventListener('modelsUpdated', checkPolish);
    return () => window.removeEventListener('modelsUpdated', checkPolish);
  }, []);

  const handlePolish = useCallback(async (textToPolish) => {
    const t = (textToPolish || text || '').trim();
    if (!t) return;
    setPolishPreview({ original: t, polished: '' });
    setPolishLocalText(t);
    setPolishEditedResult('');
    polishUserEditedRef.current = false;
    setIsPolishing(true);
    try {
      const token = getToken();
      const abortController = new AbortController();
      polishAbortRef.current = abortController;
      const response = await fetch(`${API_BASE_URL}/api/ai/text-polish`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ text: t }),
        signal: abortController.signal,
      });
      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        throw new Error(err.error || `请求失败 (${response.status})`);
      }
      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';
      let accumulated = '';
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop();
        for (const line of lines) {
          const trimmed = line.trim();
          if (!trimmed || !trimmed.startsWith('data:')) continue;
          const payload = trimmed.slice(5).trim();
          if (payload === '[DONE]') break;
          try {
            const json = JSON.parse(payload);
            if (json.error) throw new Error(json.error);
            if (json.content) {
              accumulated += json.content;
              setPolishPreview(prev => prev ? { ...prev, polished: accumulated } : null);
            }
          } catch (e) {
            if (e.message && e.message !== 'Unexpected end of JSON input') console.error(e);
          }
        }
      }
    } catch (e) {
      if (e.name === 'AbortError') return;
      setPolishPreview(prev => prev ? { ...prev, polished: `[错误] ${e.message || '整理失败'}` } : null);
    } finally {
      setIsPolishing(false);
      polishAbortRef.current = null;
    }
  }, [text]);

  useEffect(() => {
    if (!polishUserEditedRef.current && polishPreview?.polished) {
      setPolishEditedResult(polishPreview.polished);
    }
  }, [polishPreview?.polished]);

  const handlePolishConfirm = () => {
    if (polishEditedResult.trim() && onChange) onChange(polishEditedResult);
    setPolishPreview(null);
  };
  const handlePolishCancel = () => {
    if (polishAbortRef.current) polishAbortRef.current.abort();
    setPolishPreview(null);
    setIsPolishing(false);
  };
  
  // 当 forceFocusKey 变化时，强制聚焦 textarea 并设置光标到末尾
  // 这解决了全屏模式下点击序号后中文输入法候选框位置错乱的问题
  useEffect(() => {
    // 只有在 forceFocusKey 变化时才触发（即用户点击了序号/分割线按钮）
    if (forceFocusKey !== lastFocusTriggerRef.current) {
      lastFocusTriggerRef.current = forceFocusKey;
      
      // 等待下一帧，确保 DOM 已经完全更新
      requestAnimationFrame(() => {
        if (fsTextareaRef.current) {
          // 先失焦再聚焦，确保输入法状态重置
          fsTextareaRef.current.blur();
          fsTextareaRef.current.focus();
          // 设置光标到末尾
          const len = fsTextareaRef.current.value.length;
          fsTextareaRef.current.setSelectionRange(len, len);
        }
      });
    }
  }, [forceFocusKey]);
  
  if (!isOpen) return null;
  return createPortal(
    <div className="nodrag" style={{
      position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
      background: 'rgba(0,0,0,0.85)', backdropFilter: 'blur(5px)', zIndex: 999999,
      display: 'flex', alignItems: 'center', justifyContent: 'center'
    }}>
      <div style={{
        width: '900px', maxWidth: '95vw', height: '92vh',
        background: 'var(--modal-gradient)', borderRadius: '12px',
        border: '1px solid var(--border-subtle)',
        boxShadow: '0 20px 50px rgba(0,0,0,0.8)',
        display: 'flex', flexDirection: 'column'
      }}>
        <div style={{
          height: '60px', flexShrink: 0, borderBottom: '1px solid var(--border-subtle)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          position: 'relative', background: 'rgba(0,0,0,0.2)',
          borderRadius: '12px 12px 0 0'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '4px', background: 'transparent', pointerEvents: 'auto', userSelect: 'none' }}>
            <ToolbarContent {...toolbarProps} isFullMode={true} />
          </div>
          <div style={{ position: 'absolute', right: '20px', top: '50%', transform: 'translateY(-50%)', display: 'flex', alignItems: 'center', gap: '8px' }}>
            {/* 📝 润色按钮 */}
            {textPolishEnabled && onChange && (
              <div
                onClick={() => { if (!isPolishing && (text || '').trim()) handlePolish(); }}
                style={{
                  width: '36px', height: '36px', borderRadius: '50%',
                  background: isPolishing ? 'rgba(96,165,250,0.2)' : 'var(--bg-hover)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  cursor: isPolishing ? 'wait' : (text || '').trim() ? 'pointer' : 'not-allowed',
                  transition: 'all 0.2s', opacity: (text || '').trim() ? 1 : 0.45,
                }}
                onMouseEnter={(e) => { if (!isPolishing) e.currentTarget.style.background = 'rgba(96,165,250,0.25)'; }}
                onMouseLeave={(e) => { if (!isPolishing) e.currentTarget.style.background = 'var(--bg-hover)'; }}
                title="文本整理"
              >
                {isPolishing ? <Loader2 size={18} color="#60a5fa" style={{ animation: 'spin 1s linear infinite' }} /> : <Wand2 size={18} color="#60a5fa" />}
              </div>
            )}
            {/* 🎤 全屏模式下的语音输入按钮 */}
            <VoiceMicButton
              size={32}
              position="inline"
              currentText={text || ''}
              onResult={(newText) => onChange(newText)}
              textareaRef={fsTextareaRef}
            />
            <div onClick={onClose} style={{ cursor: 'pointer', padding: '8px', borderRadius: '6px', color: 'var(--text-secondary)' }}>
              <Check size={20} strokeWidth={1.5} />
            </div>
          </div>
        </div>
        <textarea
          ref={fsTextareaRef}
          data-voice-shortcut="true"
          value={text}
          onChange={(e) => onChange(e.target.value)}
          placeholder="开始编辑..."
          spellCheck={false}
          style={{
            flex: 1, padding: '40px 60px',
            background: 'transparent', border: 'none', outline: 'none', resize: 'none',
            fontSize: `${toolbarProps.fontSize}px`, 
            fontWeight: toolbarProps.fontWeight, 
            color: 'var(--text-body)', lineHeight: '1.8', fontFamily: '"PingFang SC", "Microsoft YaHei", sans-serif',
            imeMode: 'active', // 强制激活输入法模式
          }}
          onBlur={(e) => {
            // 防止意外失焦导致输入法候选框错位
            // 如果有工具栏按钮被点击，快速重新聚焦
            setTimeout(() => {
              e.target.focus();
              const len = e.target.value.length;
              e.target.setSelectionRange(len, len);
            }, 0);
          }}
        />
      </div>
      {/* 📝 文本润色预览弹窗 */}
      {polishPreview && (
        <div onClick={handlePolishCancel} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.8)', backdropFilter: 'blur(4px)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999999, padding: 20 }}>
          <div onClick={e => e.stopPropagation()} style={{ width: 900, maxWidth: '95vw', height: '92vh', background: 'var(--modal-surface)', borderRadius: 16, border: '1px solid var(--border-subtle)', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
            <div style={{ padding: '16px 20px', borderBottom: '1px solid var(--border-subtle)', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <div style={{ fontSize: 15, fontWeight: 600, color: 'var(--text-body)', display: 'flex', alignItems: 'center', gap: 8 }}>
                <Wand2 size={16} color="#60a5fa" /> 提示词整理大师
                {isPolishing && <Loader2 size={14} color="#60a5fa" style={{ animation: 'spin 1s linear infinite' }} />}
              </div>
              <div onClick={handlePolishCancel} style={{ width: 28, height: 28, borderRadius: '50%', background: 'var(--bg-hover)', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer' }}>
                <X size={16} color="var(--text-secondary)" />
              </div>
            </div>
            <div style={{ flex: 1, overflow: 'auto', padding: 20, display: 'flex', gap: 16 }}>
              <div style={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
                <div style={{ fontSize: 11, color: '#64748b', marginBottom: 8, fontWeight: 600 }}>原文</div>
                <div style={{ flex: 1, position: 'relative' }}>
                  <textarea
                    ref={polishTextareaRef}
                    data-voice-shortcut="true"
                    value={polishLocalText}
                    onChange={(e) => setPolishLocalText(e.target.value)}
                    placeholder="输入或修改文本..."
                    spellCheck={false}
                    style={{ width: '100%', height: '100%', padding: '12px 12px 40px 12px', background: 'var(--modal-input-bg)', borderRadius: 8, border: '1px solid var(--modal-input-border)', fontSize: 13, color: 'var(--text-body)', lineHeight: 1.6, resize: 'none', outline: 'none', minHeight: 200, fontFamily: 'inherit', boxSizing: 'border-box' }}
                  />
                  <div style={{ position: 'absolute', right: 8, bottom: 8, display: 'flex', gap: 4, alignItems: 'center' }}>
                    <VoiceMicButton
                      size={26}
                      position="inline"
                      currentText={polishLocalText}
                      onResult={(newText) => setPolishLocalText(newText)}
                      textareaRef={polishTextareaRef}
                    />
                    <div
                      onClick={() => { if (!isPolishing && polishLocalText.trim()) handlePolish(polishLocalText); }}
                      style={{
                        width: 26, height: 26, borderRadius: 6,
                        background: isPolishing ? 'rgba(96,165,250,0.2)' : 'var(--bg-hover)',
                        border: `1px solid ${isPolishing ? 'rgba(96,165,250,0.4)' : 'var(--border-strong)'}`,
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        cursor: isPolishing ? 'wait' : polishLocalText.trim() ? 'pointer' : 'not-allowed',
                        opacity: polishLocalText.trim() ? 1 : 0.45,
                      }}
                      title="重新整理"
                    >
                      {isPolishing ? <Loader2 size={13} color="#60a5fa" style={{ animation: 'spin 1s linear infinite' }} /> : <Wand2 size={13} color="#60a5fa" />}
                    </div>
                  </div>
                </div>
              </div>
              <div style={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
                <div style={{ fontSize: 11, color: '#60a5fa', marginBottom: 8, fontWeight: 600 }}>整理后（可编辑）</div>
                <textarea
                  data-voice-shortcut="true"
                  value={polishEditedResult}
                  onChange={(e) => { polishUserEditedRef.current = true; setPolishEditedResult(e.target.value); }}
                  placeholder={isPolishing ? '正在生成...' : ''}
                  spellCheck={false}
                  style={{ flex: 1, width: '100%', padding: 12, background: 'rgba(96,165,250,0.05)', borderRadius: 8, border: '1px solid rgba(96,165,250,0.15)', fontSize: 13, color: 'var(--text-body)', lineHeight: 1.6, resize: 'none', outline: 'none', minHeight: 200, fontFamily: 'inherit', boxSizing: 'border-box' }}
                />
              </div>
            </div>
            <div style={{ padding: '12px 20px', borderTop: '1px solid var(--border-subtle)', display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
              <button onClick={handlePolishCancel} style={{ padding: '8px 20px', borderRadius: 8, border: '1px solid var(--border-strong)', background: 'transparent', color: 'var(--text-secondary)', cursor: 'pointer', fontSize: 13 }}>取消</button>
              <button onClick={handlePolishConfirm} disabled={isPolishing || !polishEditedResult.trim()} style={{ padding: '8px 20px', borderRadius: 8, border: 'none', background: (isPolishing || !polishEditedResult.trim()) ? '#1e3a5f' : '#2563eb', color: (isPolishing || !polishEditedResult.trim()) ? '#94a3b8' : '#fff', cursor: (isPolishing || !polishEditedResult.trim()) ? 'not-allowed' : 'pointer', fontSize: 13, fontWeight: 500 }}>确认替换</button>
            </div>
          </div>
        </div>
      )}
    </div>,
    document.body
  );
};

/**
 * 当文字节点被选中但未进入编辑模式时，
 * 监听全局语音快捷键事件并通过 micRef 触发工具栏麦克风按钮。
 * 若已有 textarea 处理（isEditing / isFullView），则不介入。
 */
function VoiceShortcutForSelectedNode({ selected, isEditing, isFullView, micRef }) {
  useEffect(() => {
    // 只在选中且没有 textarea 获焦时介入
    if (!selected || isEditing || isFullView) return;
    const handleDown = (e) => {
      // textareaHandled=true 表示某个 textarea 已在处理此次快捷键，不重复录音
      if (e.detail?.textareaHandled) return;
      micRef.current?.start?.();
    };
    const handleUp = () => micRef.current?.stop?.();
    window.addEventListener(VOICE_SHORTCUT_DOWN, handleDown);
    window.addEventListener(VOICE_SHORTCUT_UP, handleUp);
    return () => {
      window.removeEventListener(VOICE_SHORTCUT_DOWN, handleDown);
      window.removeEventListener(VOICE_SHORTCUT_UP, handleUp);
    };
  }, [selected, isEditing, isFullView, micRef]);
  return null;
}

// P1: React.memo 防止父级 canvas 重渲染时级联到不相关的节点视图
export default React.memo(function TextNodeView({ id, data, selected, handleUpdate, zoom = 1, isSystemSpell = false }) {
  const [theme] = useTheme();
  const [isEditing, setIsEditing] = useState(false);
  const [isFullView, setIsFullView] = useState(false);
  const [showColorPicker, setShowColorPicker] = useState(false);
  const [focusTrigger, setFocusTrigger] = useState(0); // 用于触发全屏模式下的聚焦
  const inlineTextareaRef = useRef(null);
  const micRef = useRef(null); // 工具栏 VoiceMicButton 的外部触发句柄
  
  const text = data.prompt || '';
  const fontSize = data.fontSize || 12;
  const fontWeight = data.fontWeight || 'normal';
  const bgColor = data.bgColor || 'var(--spell-node-bg)'; // 默认底色：深色深蓝灰/浅色浅灰
  // 存储值恒为深调；浅色模式渲染时翻译成对应浅调（默认走 CSS 变量自动翻转）
  const bgColorResolved = resolveBgForTheme(bgColor, theme);
  
  const INDENT_CHAR = '\u3000\u3000'; 
  const isIndented = text.split('\n').some(line => line.startsWith(INDENT_CHAR));
  const isEmpty = !text || text.trim() === '';

  const updateData = (updates) => handleUpdate({ ...data, ...updates });

  const toggleIndent = () => {
    const lines = text.split('\n');
    let newText;
    if (isIndented) {
      newText = lines.map(line => line.startsWith(INDENT_CHAR) ? line.replace(INDENT_CHAR, '') : line).join('\n');
    } else {
      newText = lines.map(line => (line.trim() && !line.startsWith(INDENT_CHAR)) ? `${INDENT_CHAR}${line}` : line).join('\n');
    }
    updateData({ prompt: newText });
  };

  const setPreset = (type) => {
    switch(type) {
      case 'h1': updateData({ fontSize: 24, fontWeight: 'bold' }); break;
      case 'h2': updateData({ fontSize: 20, fontWeight: 'bold' }); break;
      case 'h3': updateData({ fontSize: 16, fontWeight: 'bold' }); break;
      case 'p':  updateData({ fontSize: 12, fontWeight: 'normal' }); break;
      default: break;
    }
  };

  const insertDivider = () => {
    const prefix = text.endsWith('\n') ? '' : '\n';
    const newText = text + prefix + '---\n';
    updateData({ prompt: newText });
    setIsEditing(true);
    
    // 修复全屏模式下中文输入法光标位置问题
    if (isFullView) {
      setFocusTrigger(prev => prev + 1);
    }
  };

  const insertNumbering = () => {
    const lines = text.split('\n');
    const lastLine = lines[lines.length - 1].trim();
    const match = lastLine.match(/^(\d+)\./); 
    let nextNum = 1;
    if (match) nextNum = parseInt(match[1]) + 1;
    const prefix = text.endsWith('\n') ? '' : '\n';
    const newText = text + prefix + `${nextNum}. `;
    updateData({ prompt: newText });
    setIsEditing(true);
    
    // 修复全屏模式下中文输入法光标位置问题
    // 通过递增 focusTrigger 触发 FullScreenModal 内的 useEffect 来聚焦
    if (isFullView) {
      setFocusTrigger(prev => prev + 1);
    }
  };

  const copyText = (e) => { e.stopPropagation(); if (text) copyTextToClipboard(text); };

  const nodeContainerStyle = {
    ...styles.textNodeArea,
    width: '100%', height: '100%', boxSizing: 'border-box',
    lineHeight: '1.6', overflowY: 'auto', overflowX: 'hidden',
    fontSize: `${fontSize}px`, fontWeight: fontWeight,
    backgroundColor: bgColorResolved,
    borderRadius: '8px',
    display: isEmpty && !isEditing ? 'flex' : 'block',
    alignItems: 'center', justifyContent: 'center',
    padding: '16px', color: 'var(--text-node-body)', whiteSpace: 'pre-wrap',
    border: isEditing ? '1px solid rgba(255,255,255,0.2)' : 'none',
    textAlign: 'justify' 
  };

  const capsuleStyle = {
    display: 'flex', alignItems: 'center', gap: '4px',
    background: 'var(--toolbar-bg)',
    border: '1px solid var(--border-subtle)',
    borderRadius: '50px', padding: '6px 16px',
    boxShadow: '0 4px 20px rgba(0, 0, 0, 0.4)',
    pointerEvents: 'auto', width: 'max-content',
    userSelect: 'none',
    backdropFilter: 'blur(8px)' 
  };

  const renderContent = () => {
    if (!text) return <span style={{ opacity: 0.5, fontSize: '14px', pointerEvents: 'none' }}>双击输入文本</span>;
    return text.split('\n').map((line, idx) => {
      if (line.trim() === '---') return <div key={idx} style={{ height: '1px', background: 'rgba(255,255,255,0.2)', margin: '16px 0' }} />;
      return <div key={idx} style={{ minHeight: '1.6em' }}>{line}</div>;
    });
  };

  const toolbarProps = {
    fontSize, fontWeight, isIndented, showColorPicker, bgColor, theme,
    toggleColorPicker: () => setShowColorPicker(!showColorPicker),
    updateBgColor: (c) => { updateData({ bgColor: c }); setShowColorPicker(false); },
    setPreset, toggleIndent, insertDivider, insertNumbering, copyText,
    toggleFullScreen: () => setIsFullView(true)
  };

  return (
    <>
      {/* 文字节点选中但未进入编辑模式时，监听全局语音快捷键事件，通过 micRef 触发工具栏麦克风 */}
      <VoiceShortcutForSelectedNode selected={selected} isEditing={isEditing} isFullView={isFullView} micRef={micRef} />
      <FullScreenModal 
        isOpen={isFullView} 
        onClose={() => setIsFullView(false)} 
        text={text} 
        onChange={isSystemSpell ? undefined : (val) => updateData({ prompt: val })} 
        toolbarProps={toolbarProps} 
        forceFocusKey={focusTrigger}
      />
      {isSystemSpell ? (
        <div style={{ ...nodeContainerStyle, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'default', userSelect: 'none' }}>
          <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="rgba(255,255,255,0.18)" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="11" width="18" height="11" rx="2" ry="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/></svg>
        </div>
      ) : isEditing ? (
        <textarea ref={inlineTextareaRef} data-voice-shortcut="true" className="nodrag nowheel" value={text} onChange={(e) => updateData({ prompt: e.target.value })} onBlur={() => setIsEditing(false)} autoFocus placeholder="输入文本..." style={{ ...nodeContainerStyle, resize: 'none', outline: 'none', color: '#fff' }} />
      ) : (
        <div className="nowheel" onDoubleClick={(e) => { e.stopPropagation(); setIsEditing(true); }} style={{ ...nodeContainerStyle, cursor: 'grab', userSelect: 'text' }}>
          {renderContent()}
        </div>
      )}
      {selected && !isFullView && !isSystemSpell && (
        <div className="nodrag" style={{ position: 'absolute', top: '100%', left: '0', right: '0', height: '0', zIndex: 999 }}>
          <div style={{ position: 'absolute', top: '2px', left: '50%', transform: `translateX(-50%) scale(${(1 / zoom) * 0.9})`, transformOrigin: 'top center', ...capsuleStyle }}>
            <ToolbarContent {...toolbarProps} />
            {/* 🎤 语音输入按钮 */}
            <VoiceMicButton
              size={28}
              position="inline"
              currentText={text || ''}
              onResult={(newText) => updateData({ prompt: newText })}
              textareaRef={inlineTextareaRef}
              triggerRef={micRef}
              style={{ marginLeft: '2px' }}
            />
          </div>
        </div>
      )}
    </>
  );
});